import React, {
  useCallback,
  useState,
  useImperativeHandle,
  useEffect,
} from 'react';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import styled from 'styled-components';
import IndicatorBottomButtons from '../IndicatorBottomButtons';
import { IndicatorDetailSectionProps, IndicatorDetails } from '../utils';
import IndicatorMeasurementSelection from './IndicatorMeasurementSelection';
import { IndicatorDetailsHandler } from '../../../../NPOFeedback/FeedbackDetails/ImpactDetailsEdit/utils';
import { getIndicatorDetailsMeasurement } from './utils';
import { MeasurementTypeSupportText } from '../../ImpactIndicator/SupportText';
import {
  getIndicatorSentence,
  getMeasurementSentence,
} from '../../ImpactIndicator/IndicatorMadLibs';

const StyledDiv = styled.div`
  .guess-heading {
    max-width: 530px;
    margin-left: auto;
    margin-right: auto;
    margin-bottom: 40px;
  }
  .support-text {
    margin-bottom: ${SPACERS.large};
  }
  .selection-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    button {
      width: 100%;
      display: block;
      font-size: 16px;
      background-color: transparent;
    }
    .selection-margin {
      margin-bottom: ${SPACERS.small};
    }
    .first-grid {
      padding-right: ${SPACERS.medium};
      padding-left: ${SPACERS.medium};
    }
    .second-grid {
      padding-left: ${SPACERS.medium};
      padding-right: ${SPACERS.medium};
      border-left: 1px solid ${COLORS.greyBase};
      border-right: 1px solid ${COLORS.greyBase};
    }
    .third-grid {
      padding-left: ${SPACERS.medium};
      padding-right: ${SPACERS.medium};
    }
    .selection {
      cursor: pointer;
      text-align: left;
      border-radius: 4px;
      padding: ${SPACERS.small};
      transition: background 0.5s;
      border: 1px solid ${COLORS.ctaPrimary};
      &:hover {
        background-color: ${COLORS.highlightBackground};
      }
      &.selected {
        color: ${COLORS.whiteBase};
        background-color: ${COLORS.ctaPrimary};
        .icon {
          color: ${COLORS.whiteBase};
        }
      }
    }
  }
`;

const validateMeasurementNext = (
  measurement: IndicatorDetails['measurement'],
) => Boolean(measurement);

const IndicatorMeasurement = React.forwardRef<
  IndicatorDetailsHandler,
  IndicatorDetailSectionProps & {
    indicatorDetails: IndicatorDetails;
  }
>(
  (
    {
      indicator,
      hideBottomButtons,
      requestState,
      indicatorDetails,
      selectedIndex,
      programName,
      allIndicators,
      reportPeriod,
      intermediaries,
      serviceGoal,
      serviceType,
      isReportPassed,
      beneficiaryTerm,
      selectedSectionKey,
      className,
      handleIndicatorSave,
      setIndicators,
      onNext,
      onPrevious,
    },
    forwardRef,
  ) => {
    const [measurement, setMeasurement] = useState<string | undefined>(
      indicatorDetails.measurement,
    );

    useEffect(() => {
      setMeasurement(indicatorDetails.measurement);
    }, [indicatorDetails]);

    useImperativeHandle(forwardRef, () => ({
      answer: () => ({
        answer: measurement,
        errorText: validateMeasurementNext(measurement)
          ? undefined
          : 'Please select a valid measurement option.',
      }),
    }));

    const handleNext = useCallback(
      (newMeasurement?: string) => {
        const value =
          newMeasurement === undefined ? measurement : newMeasurement;
        onNext({ measurement: value });
        setIndicators(s => {
          const newState = { ...s };
          newState[selectedSectionKey][selectedIndex].measurement = value;
          return newState;
        });
        const newIndicatorDetails = { ...indicatorDetails };
        newIndicatorDetails.measurement = value;
        handleIndicatorSave(newIndicatorDetails);
      },
      [
        measurement,
        selectedIndex,
        indicatorDetails,
        selectedSectionKey,
        onNext,
        setIndicators,
        handleIndicatorSave,
      ],
    );

    const handleClick = (newMeasurement: IndicatorDetails['measurement']) => {
      setMeasurement(newMeasurement);
      /*if (measurement === undefined) {
      handleNext(newMeasurement);
    }*/
    };

    const sentence =
      getMeasurementSentence(
        allIndicators[indicatorDetails.indicatorId],
        programName,
        beneficiaryTerm,
        intermediaries,
        isReportPassed,
        reportPeriod,
        serviceGoal,
        serviceType,
        selectedSectionKey,
        indicatorDetails.forecast,
        indicatorDetails.finalForecast,
      ) ||
      getIndicatorSentence(
        indicator || { id: indicatorDetails.indicatorId },
        isReportPassed ? 'MeasurementFinal' : 'MeasurementInitial',
        indicatorDetails.indicatorName,
        selectedSectionKey,
        indicatorDetails.forecast,
        beneficiaryTerm,
      );
    return (
      <StyledDiv className={className}>
        <TrueImpactText variant="heading-page" className="guess-heading">
          {sentence}
        </TrueImpactText>
        <MeasurementTypeSupportText
          sectionKey={selectedSectionKey}
          isReportPassed={isReportPassed}
          className="support-text"
        />
        <div className="selection-grid">
          <div className="first-grid">
            <TrueImpactText
              variant="heading-subheading"
              className="selection-heading"
            >
              Direct Measurement
            </TrueImpactText>
            {getIndicatorDetailsMeasurement(
              selectedSectionKey,
              'Direct Measurement',
            ).map(t => (
              <IndicatorMeasurementSelection
                key={t}
                text={t}
                selectedValue={measurement}
                onClick={handleClick}
                className="selection selection-margin"
              />
            ))}
          </div>
          <div className="second-grid">
            <TrueImpactText
              variant="heading-subheading"
              className="selection-heading"
            >
              Estimate Based on Data
            </TrueImpactText>
            {getIndicatorDetailsMeasurement(
              selectedSectionKey,
              'Estimate Based on Data',
            ).map(t => (
              <IndicatorMeasurementSelection
                key={t}
                text={t}
                selectedValue={measurement}
                onClick={handleClick}
                className="selection selection-margin"
              />
            ))}
          </div>
          <div className="third-grid">
            <TrueImpactText
              variant="heading-subheading"
              className="selection-heading"
            >
              Guess
            </TrueImpactText>
            {getIndicatorDetailsMeasurement(selectedSectionKey, 'Guess').map(
              t => (
                <IndicatorMeasurementSelection
                  key={t}
                  text={t}
                  selectedValue={measurement}
                  onClick={handleClick}
                  className="selection selection-margin"
                />
              ),
            )}
          </div>
        </div>
        {!hideBottomButtons && (
          <IndicatorBottomButtons
            requestState={requestState}
            disabledNext={!validateMeasurementNext(measurement)}
            onNext={() => handleNext()}
            onPrevious={onPrevious}
          />
        )}
      </StyledDiv>
    );
  },
);

export default IndicatorMeasurement;
